import i18next from 'i18next';
import { format, isValid } from 'date-fns';
import { grey } from '@mui/material/colors';
import {
  FormType,
  FormStepType,
  FormStatus,
  CLOSED,
  Question,
  FormSkipTypes,
  Step,
  NOT_ASSIGNED_TO_APPROVAL_STEP,
  InputType,
  FormItemResponse,
} from 'src/types/forms';
import { logEvent } from 'src/config/logging';
import { EventName } from 'src/types/amplitude';
import { Languages } from 'src/types/translations';
import { SpecialFields } from 'src/types/forms';
import { sortByPosition } from 'src/utils/position';
import { LOCALE } from 'src/constants';

const { EN, ES } = Languages;

const removeQueryParamMapper = values =>
  values.map(e => ({ ...e, url: e.url?.split('?')[0] }));

export const formatValue = (key, value) => {
  if (key === 'phoneCode') return null;
  if (!value && value !== 0) return null;

  const keyFragments = key.split('-');
  const inputType = keyFragments[2];

  const propertiesAnswer = {
    id: Number(keyFragments[4]),
    inputType,
  };

  if (inputType === InputType.CHECKBOX && keyFragments[3] === 'true') {
    if (!Object.values(value).length) return null;
    return {
      ...propertiesAnswer,
      answer: Object.values(value),
    };
  }
  if (inputType === InputType.FILE) {
    return {
      ...propertiesAnswer,
      attachments: removeQueryParamMapper(value),
    };
  }
  if (inputType === InputType.MEDIA) {
    if (value?.images?.length === 0 && value?.videos?.length === 0) {
      return null;
    }

    return {
      ...propertiesAnswer,
      attachments: [
        ...(Array.isArray(value.images)
          ? removeQueryParamMapper(value.images)
          : []),
        ...(Array.isArray(value.videos)
          ? removeQueryParamMapper(value.videos)
          : []),
      ],
    };
  }
  if (inputType === InputType.DIGITAL_SIGN) {
    return {
      ...propertiesAnswer,
      attachments: removeQueryParamMapper(value.answer),
    };
  }
  if (inputType === InputType.DATE) {
    return {
      ...propertiesAnswer,
      answer: [format(value, 'dd/MM/yyyy')],
    };
  }
  if (inputType === InputType.DATE_MMAAAA) {
    return {
      ...propertiesAnswer,
      answer: [format(value, 'MM/yyyy')],
    };
  }
  if (inputType === InputType.TIME) {
    return {
      ...propertiesAnswer,
      answer: [format(value, 'HH:mm')],
    };
  }
  if (inputType === InputType.DECIMAL) {
    return {
      ...propertiesAnswer,
      answer: [value.toString()],
    };
  }
  if (inputType === InputType.PHONE) {
    if (value.length <= 4) return null;

    return {
      ...propertiesAnswer,
      answer: [value],
    };
  }
  if (inputType === InputType.CHECKBOX_GRID) {
    const rows = [];

    const entries = Object.entries(value);

    if (!entries.length) return null;

    entries.forEach(([id, answer]) => rows.push({ id: Number(id), answer }));

    return {
      ...propertiesAnswer,
      rows,
    };
  }

  return {
    ...propertiesAnswer,
    answer: [value],
  };
};

export const formatDecimalAnswer = (value, lang) =>
  lang === ES ? value?.replace('.', ',') : value;

export const formatValues = values =>
  Object.keys(values)
    .map(key => formatValue(key, values[key]))
    .filter(value => value !== null);

export const buildCheckboxGridAnswer = answerInput => {
  if (!answerInput) return null;

  const answers = answerInput.answer;
  if (!answers) return null;

  const builtOptions = answers.reduce((answerObj, current) => {
    const options: string[] = [];

    current?.answer?.forEach(option => {
      if (option.checked) {
        options.push(option.description);
      }
    });

    if (!options.length) return answerObj;

    return {
      ...answerObj,
      [current.rowId]: options,
    };
  }, {});

  if (!Object.keys(builtOptions).length) return null;

  return builtOptions;
};

export const buildOptionsToDropDown = options =>
  options.map(opt => ({ value: opt.description, label: opt.description }));

export const buildOptionsToDropDownCatalog = options =>
  options.map((opt, index) => ({ value: index, label: opt }));

export const buildOptionsToRadioButton = options =>
  options.map(opt => ({ value: opt.description, label: opt.description }));

export const getDayMonthYear = answerDate => {
  const dateParts = answerDate.split('/');
  return new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
};

export const getMonthYear = answerDate => {
  const dateParts = answerDate.split('/');
  return new Date(dateParts[1], dateParts[0] - 1);
};

export const formatAnswerCheckbox = answerCheckbox => {
  const splitAnswerCheckbox = answerCheckbox.split('-');
  let result = '';
  for (let i = 0; i < splitAnswerCheckbox.length - 1; i++) {
    if (i !== 0) {
      result += '-';
    }
    result += splitAnswerCheckbox[i];
  }
  return result;
};

export const formatRouteForms = path => `${path.toLowerCase()}s`;

// TODO: esto debera cambiar cuando el back implemente correctamente la solucion
export const getCorrectStatus = (isAgent, approvalStatus, lastMessageType) => {
  if (!isAgent) return approvalStatus;
  if (approvalStatus === CLOSED) {
    return lastMessageType;
  }
  return approvalStatus;
};

export const getLabel = (needApproval, approvalStatus) => {
  if (!needApproval) {
    return i18next.t('FORMS:NO_NEED_APPROBATION');
  }

  if (
    approvalStatus === FormStatus.APPROVED ||
    approvalStatus === FormStatus.REJECTED
  ) {
    return i18next.t(`FORMS:FORM_STATUS_${approvalStatus}`);
  }

  if (approvalStatus === FormStatus.CANCELLED) {
    return i18next.t(`FORMS:FORM_STATUS_${approvalStatus}`);
  }

  return i18next.t('FORMS:NEED_APPROBATION');
};

export const createBulkBody = items =>
  items.reduce(
    (acc, elem) => ({
      chatIds: elem.chatId ? [...acc.chatIds, elem.chatId] : acc.chatIds,
      approvalWorkflows: [
        ...acc.approvalWorkflows,
        { formId: elem.formId, stepId: elem.stepId },
      ],
    }),
    { chatIds: [], approvalWorkflows: [] },
  );

export const getBackgroundColor = (needApproval, isAgent) => {
  if (!isAgent) return grey[500];
  return !needApproval ? grey[500] : grey[700];
};

export const getInfoText = text => {
  const [status, ...nameArray] = text.split(',');

  const STATUS = {
    ASSIGNED_QUERY: 'FORMS:ASSIGNED_QUERY',
    CLOSED: 'FORMS:FINALIZED_QUERY',
  };

  return i18next.t(STATUS[status], {
    responsible: nameArray.join(','),
  });
};

export const getKey = (name = '') => ['forms', `${name.toLowerCase()}Forms`];

export const getStatusFormForRequest = (approveForm, rejectForm) => {
  if (approveForm) return FormStatus.APPROVED;
  if (rejectForm) return FormStatus.REJECTED;
  return null;
};

export const getPlaceholderString = viewsArray => {
  if (viewsArray !== undefined && viewsArray[2] === undefined) {
    return 'FORMS:FORM_DATEPICKER_PLACEHOLDER_MONTH_YEAR';
  }
  return 'FORMS:FORM_DATEPICKER_PLACEHOLDER_DATE';
};

export const getQuestions = (questions: Record<number, Question>): Question[] =>
  sortByPosition(Object.values(questions));

export const getQuestionName = (question: Question): string =>
  `${question.inputType}-${question.multipleAnswer}-${question.id}`;

export const hasMandatory = (questions: Question[]): boolean =>
  questions?.filter(q => q.required).length > 0;

export const getUnreadCompletedFormsWithPages = (
  completedForms: any[] = [],
): number => {
  let counter = 0;

  completedForms.forEach(formPage => {
    formPage?.data.forEach(formItem => {
      if (formItem.unreadMessages > 0) {
        counter++;
      }
    });
  });

  return counter;
};

export const findFormIndex = (chatItemId: number, formDataArray: any[]) =>
  formDataArray.findIndex(element => element.chatId === chatItemId);

export const findFormIndexOnPages = (
  id: number,
  pages: any[],
): [pageIndex: number, formIndex: number] => {
  for (let i = 0; i < pages.length; i++) {
    const formIndex = pages[i].data.findIndex(form => form.chatId === id);
    if (formIndex !== -1) return [i, formIndex];
  }
  return [-1, -1];
};

export const isFormReadyToSubmit = (currentStep, stepsCount, stepsList) => {
  const isPreSubmitStep = currentStep + 1 === stepsCount;
  const isLastStep = currentStep + 2 === stepsCount;
  const currentStepData = stepsList[currentStep];

  const allQuestionsHaveNullOrEndSkipType = !currentStepData?.questions.some(
    question =>
      question.options?.some(
        option => option.skip && option.skip.skipType !== FormSkipTypes.END,
      ),
  );
  const stepSkipTypeIsEnd =
    currentStepData?.skip?.skipType === FormSkipTypes.END;

  const noQuestionHasSkip = !currentStepData?.questions?.some(question =>
    question.options?.some(option => option.skip),
  );

  return (
    ((isLastStep || isPreSubmitStep) && allQuestionsHaveNullOrEndSkipType) ||
    (stepSkipTypeIsEnd && noQuestionHasSkip)
  );
};

export const buildStepItem = (steps, stepToGo = 0, optionId = undefined) => ({
  stepIndex: stepToGo,
  skip: steps[stepToGo]?.skip,
  stepId: steps[stepToGo]?.id,
  optionId,
});

export const findStepToGo = (steps, destinationStepId) =>
  steps?.findIndex(elem => elem.id === destinationStepId);

export const getOptionSelected = (options, value) =>
  options.find(opt => opt.description === value);

export const getStepById = (steps, stepId) =>
  steps.find(step => step.id === stepId);

export const getPositionQuestionByOptionId = (questions, optionId) =>
  questions.find(q => q.options && q.options.find(opt => opt.id === optionId))
    ?.position;

export const makePreSubmitStep = () => ({
  id: `${Math.random()}-pre-submit`,
  questions: [],
});

export const modifyStackStepForms = (
  options,
  value,
  stackStepForms,
  stepsList,
) => {
  const optionSelected = getOptionSelected(options, value);
  const currentStepInStack = stackStepForms[stackStepForms?.length - 1];

  if (!currentStepInStack.optionId && optionSelected.skip) {
    currentStepInStack.skip = optionSelected.skip;
    currentStepInStack.optionId = optionSelected.id;
  } else if (optionSelected.skip) {
    const step = getStepById(stepsList, currentStepInStack.stepId);
    const positionCurrentStepInStack = getPositionQuestionByOptionId(
      step.questions,
      currentStepInStack.optionId,
    );

    const positionOptionSelected = getPositionQuestionByOptionId(
      step.questions,
      optionSelected.id,
    );

    if (
      (positionOptionSelected >= positionCurrentStepInStack &&
        (optionSelected.skip || !currentStepInStack.skip)) ||
      (positionOptionSelected < positionCurrentStepInStack &&
        !currentStepInStack.skip)
    ) {
      currentStepInStack.skip = optionSelected.skip;
      currentStepInStack.optionId = optionSelected.id;
    }
  }
};

export const resetValuesGoBack = (
  formValues,
  destinationStepIds,
  resetField,
) => {
  Object.keys(formValues).forEach(formValueItem => {
    const formValueItemSplit = formValueItem.split('-');
    if (destinationStepIds.includes(parseInt(formValueItemSplit[5]))) {
      resetField(formValueItem);
    }
  });
};

export const childDestinations = (
  formDetailData,
  currentId,
  stopId,
  destinationStepIds,
) => {
  const stopIds = [];
  stopIds.push(stopId);
  formDetailData.steps.forEach(item => {
    if (item.id === stopId) {
      if (item.skip) {
        stopIds.push(item.skip.destinationStepId);
      }
    }
  });
  formDetailData.steps.forEach(item => {
    if (item.id === currentId) {
      if (item.skip) {
        destinationStepIds.push(item.skip.destinationStepId);
      } else if (!stopIds.includes(currentId + 1)) {
        destinationStepIds.push(currentId + 1);
      }
    }
  });
  return destinationStepIds;
};

export const getDestinationStepIds = (step: Step) => {
  let destinationStepIds = [];
  step.questions.forEach(item => {
    if (item.options) {
      item.options.forEach(elem =>
        destinationStepIds.push(elem.skip?.destinationStepId),
      );
    }
  });
  return destinationStepIds;
};

export const handleErrorToAddComment = error => {
  if (error.response?.data?.code === NOT_ASSIGNED_TO_APPROVAL_STEP) {
    return i18next.t('FORMS:NOT_ASSIGNED_TO_APPROVAL_STEP');
  }
  return i18next.t('FORMS:ERROR_ADD_COMMENT');
};

export const userFields = {
  COMPLETE_NAME: 'name',
  BIRTHDATE: 'birthdate',
  EMAIL: 'email',
  COMPLETION_DATE: 'COMPLETION_DATE',
  EMPLOYEE_INTERNAL_ID: 'employeeInternalId',
  HIRING_DATE: 'hiringDate',
  DIRECT_BOSS: 'DIRECT_BOSS',
};

export const getAnswerAutocompleteField = (
  userField,
  autoCompleteBoss,
  user,
  t,
) => {
  let answer;

  const profileField = userFields[userField];

  const customFieldValue = user.profileData[userField];

  if (profileField) {
    answer =
      profileField === userFields.COMPLETE_NAME
        ? `${user.firstName} ${user.lastName}`
        : user[profileField];
  } else if (customFieldValue || customFieldValue === 0) {
    answer = Array.isArray(customFieldValue)
      ? customFieldValue.join(', ')
      : customFieldValue;
  }

  if (userField === userFields.DIRECT_BOSS) {
    answer = autoCompleteBoss;
  }

  let dateToValidate;

  if (userField === userFields.COMPLETION_DATE) {
    dateToValidate = new Date();
  } else if (typeof answer === 'number') {
    dateToValidate = answer.toString;
  } else {
    dateToValidate = answer;
  }

  if (isValid(dateToValidate)) {
    const currentLanguage = LOCALE[user.language];
    const isEnglish = currentLanguage.code.includes(EN);
    answer = format(dateToValidate, isEnglish ? 'MM/dd/yyyy' : 'dd/MM/yyyy');
  }

  return answer || answer === 0 ? answer : t('EMPTY');
};

export const getFilteredValues = objValues => {
  const filteredPairs = Object.entries(objValues).filter(
    ([key]) => !key.includes('AUTOCOMPLETE'),
  );
  return Object.fromEntries(filteredPairs);
};

export const checkEmptyForm = objValues =>
  !objValues.some(
    value =>
      value !== undefined &&
      value !== '' &&
      value !== null &&
      (typeof value !== 'object' || Object.keys(value).length !== 0),
  );

export const checkEmptyObject = obj => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const isEmptyObject =
        Object.prototype.toString.call(value) === '[object Object]' &&
        Object.keys(value).length === 0;
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !isEmptyObject &&
        !isEmptyArray
      ) {
        return false;
      }
    }
  }
  return true;
};

export const getThousandSeparator = lang => (lang === 'en' ? ',' : '.');

export const getDecimalSeparator = lang => (lang === 'en' ? '.' : ',');

export const getCurrentProgress = (totalSteps, currentStep) => {
  const remainder = 100 / totalSteps;
  const multiple = 100 - remainder;
  return (currentStep * multiple) / totalSteps;
};

export const logAmplitudeEvents = (formDetailData, response) => {
  if (formDetailData?.type === FormType.SURVEY) {
    logEvent(EventName.SURVEY_ANSWER, {
      surveyFilledId: response.data?.form.id,
      surveyId: response.data?.form.templateId,
      surveyName: response.data?.form.title,
    });
  }

  if (formDetailData?.type === FormType.FORM) {
    if (formDetailData?.needApproval) {
      logEvent(EventName.FORM_APPROVAL_FILL, {
        formFilledId: response.data?.id,
        formName: response.data?.title,
        formId: response.data?.templateId,
        formPDF: formDetailData.stepsType === FormStepType.FILE,
      });
    } else {
      logEvent(EventName.FORM_REGULAR_FILL, {
        formFilledId: response.data?.form.id,
        formName: response.data?.form.title,
        formId: response.data?.form.templateId,
        formPDF: formDetailData.stepsType === FormStepType.FILE,
      });
    }
  }
};

export const logAmplitudeEditEvent = (formFilledId, needApproval, formId) =>
  logEvent(
    needApproval ? EventName.FORM_APPROVAL_EDIT : EventName.FORM_REGULAR_EDIT,
    { formFilledId, formId },
  );

export const getDateFormat = user => {
  const currentLanguage = LOCALE[user.language];
  if (!currentLanguage) return 'DD/MM/YYYY';

  return currentLanguage.code.includes(EN) ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
};

export const getDueDate = (
  isUnassigned,
  isAssigned,
  lastFilledStep: FormItemResponse['lastFilledStep'],
) => {
  if (isUnassigned) {
    return lastFilledStep?.assignedDatetimeLimit;
  }
  return isAssigned
    ? lastFilledStep?.reviewDatetimeLimit
    : lastFilledStep?.reviewDatetime;
};

export const getInputTooltipTitle = (
  value,
  description,
  required,
  inputLimit,
) => {
  let showDescription = description;
  if (required) {
    showDescription = `* ${showDescription}`;
  }
  if (inputLimit) {
    showDescription = `${showDescription} • (${value.length}/${inputLimit})`;
  }

  return showDescription;
};

export const getSLATime = (sla, elapsedTime, trackedTime, isClosed) => {
  let remainingTime;
  if (isClosed) {
    remainingTime = trackedTime / 1000;
  } else {
    remainingTime = (sla - elapsedTime) / 1000;
  }

  const days = Math.floor(remainingTime / (24 * 3600));
  let aux = remainingTime % (24 * 3600);
  const hours = Math.floor(aux / 3600);
  aux = aux % 3600;
  const minutes = Math.floor(aux / 60);

  if (days >= 1) {
    return `${days}d ${hours}h`;
  } else if (hours >= 1) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

type ColorType =
  | 'error'
  | 'success'
  | 'warning'
  | 'inherit'
  | 'disabled'
  | 'action'
  | 'secondary'
  | 'primary'
  | 'info';

export const getSLAColor = (sla, elapsedTime): ColorType => {
  const remainingTime = sla - elapsedTime;
  if (remainingTime < 0) {
    return 'error';
  } else if (elapsedTime <= 0.15 * sla) {
    return 'success';
  } else {
    return 'warning';
  }
};

export const isSpecialForm = (instanceId, formName, currentStep) =>
  (instanceId === 42079 || instanceId === 24496) &&
  (formName === 'RRHH - Pago de Reemplazo' ||
    (formName === 'RECATEGORIZACIÓN DE PERSONAL' && currentStep === 0));

export const isSpecialField = (description: string) =>
  Object.values(SpecialFields).includes(description as SpecialFields);
